import { Button } from '@accure/ui';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ChevronDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { LogOut, RefreshCcw } from 'lucide-react';
import { useBoolean } from 'usehooks-ts';

export type GlobalErrorProps = {
  error: unknown;
  eventId?: string;
  resetError(): void;
};

export function GlobalError({ error, eventId }: GlobalErrorProps) {
  const expanded = useBoolean(false);
  const auth0 = useAuth0();

  function handleRefresh() {
    window.location.reload();
  }

  async function handleLogout() {
    await auth0.logout({
      logoutParams: { returnTo: window.location.origin },
    });
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-100">
      <div className="mb-4 w-full max-w-2xl space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-8">
        <div className="flex items-center justify-center">
          <ExclamationTriangleIcon className="mr-4 size-12 text-red-500" />
          <h1 className="text-3xl font-bold text-gray-900">
            Oops! Something went wrong
          </h1>
        </div>

        <p className="text-center text-gray-600">
          We apologize for the inconvenience. An unexpected error has occurred.
        </p>

        {error instanceof Error || eventId ? (
          <div>
            {expanded.value ? (
              <div className="mt-4 rounded-lg bg-gray-100 p-4 text-left text-sm text-gray-700">
                {error instanceof Error ? (
                  <>
                    <p className="mb-2 font-semibold">Error Message:</p>
                    <p className="mb-4">{error.message}</p>
                    <p className="mb-2 font-semibold">Stack Trace:</p>
                    <pre className="mb-4 whitespace-pre-wrap">
                      {error.stack}
                    </pre>
                  </>
                ) : null}
                {eventId ? (
                  <>
                    <p className="mb-2 font-semibold">Event ID:</p>
                    <p>{eventId}</p>
                  </>
                ) : null}
              </div>
            ) : (
              <button
                onClick={expanded.setTrue}
                className="flex w-full items-center justify-between rounded-lg bg-gray-100 p-4 focus:outline-none"
              >
                <span className="font-semibold text-gray-700">
                  Error Details
                </span>
                <ChevronDownIcon className="ml-2 size-5 shrink-0" />
              </button>
            )}
          </div>
        ) : null}

        <div className="mx-auto flex w-96 flex-col gap-3 pt-8">
          <Button size="lg" onClick={handleRefresh} className="w-full">
            <RefreshCcw />
            Refresh Page
          </Button>
          <Button
            size="lg"
            onClick={handleLogout}
            variant="primaryOutline"
            className="w-full"
          >
            <LogOut />
            Refresh Session
          </Button>
        </div>
      </div>
    </div>
  );
}
